import { AfterViewInit, Component, computed, effect, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, signal, SimpleChanges, ViewChild, ViewChildren } from "@angular/core";
import { IMPORTS } from "./_imports";
import { FlowDefinitionItemComponent } from "../../iterables/flow-definition-item/flow-definition-item.component";
import { Unidade } from "src/app/models/organograma.model";
import { FlowDefinition, FlowDefinitionContext } from "src/app/models/flow.model";
import { FlowInboxDirectory } from "src/app/models/flow-inbox-directory.model";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { Utils } from "src/app/shared/utils";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { ConfirmationService } from "primeng/api";
import { NameFilter, IsActiveFilter, IsPublishedFilter } from "src/app/pages/flow-definition-list/flow-definition-list.pipe";
import { AcessoCidadaoService } from "src/app/services/acesso-cidadao.service";
import { AuthService } from "src/app/services/auth.service";
import { FlowDefinitionContextService } from "src/app/services/flow-definition-context.service";
import { FlowDefinitionService } from "src/app/services/flow-definition.service";
import { FlowInboxDirectoryService } from "src/app/services/flow-inbox-directory.service";
import { FlowObjectDefinitionService } from "src/app/services/flow-object-definition.service";
import { Location } from '@angular/common';
import { Enums } from "src/app/shared/enums";
import { FlowRules } from "src/app/shared/flow-rules";
import { SCSS_VARS } from "src/app/shared/shared-scss-ts-variables";
import { BaseComponent } from "../../base/base.component";

@Component({
    selector: 'canvas-list',
    templateUrl: './canvas-list.component.html',
    styleUrl: './canvas-list.component.scss',
    standalone: true,
    imports: IMPORTS
})
export class CanvasListComponent extends BaseComponent implements OnInit, AfterViewInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('inputFilterRef') inputFilterRef: ElementRef<HTMLInputElement>;
    @ViewChildren('flowContextWrapperRef') flowContextWrapperRef: QueryList<ElementRef>;
    @ViewChildren('contextRef') contextRef: QueryList<ElementRef>;
    @ViewChildren('flowItemRef') flowItemRef: QueryList<FlowDefinitionItemComponent>;
    @ViewChildren('editContextButtonRef') editContextButtonRef: QueryList<ElementRef>;
    @ViewChildren('editContextInputRef') editContextInputRef: QueryList<ElementRef>;
    // #endregion

    // #region [const]
    CONTEXT_MAX_LIMIT = 5 as const;
    selectedFlowInboxConfig: any = {
        search: true,
        searchOnKey: 'description',
        displayKey: 'description',
        placeholder: 'Buscar',
        searchPlaceholder: 'Filtrar',
        moreText: 'Mais',
        noResultsFound: 'Nenhum resultado encontrado',
        height: '500px'
    } as const;
    // #endregion

    // #region [props]
    contextModeClickExclusionElements: any[] = [];
    reorderedFlowDefinitionIds: string[] = [];
    avoidedInitialContextClick = false;
    hasContextNameChanged = false;
    shouldWaitForAcessoCidadaoRenewal = false;
    adminUnitsList: Unidade[] = [];
    flowInboxDirectoryMode = Enums.FlowInboxDirectoryMode;
    // #endregion

    // #region [signals]
    $isRemovingFromDirectory = signal(false);
    $flowDefinitionContexts = signal([] as FlowDefinitionContext[]);
    $contextModeDispatcher = signal(null as FlowDefinition); /* flow-definition-item onde foi clicada a ação para entrar no modo Context */
    $flowDefinitions = signal(null as FlowDefinition[]);
    $filteredFlowDefinitions = signal([] as FlowDefinition[]);
    $currentEditContextId = signal(null as string);
    $isBootstrapFinished = signal(false);
    $isFlowItemVisible = signal(false);
    $isContextMode = signal(false);
    $shouldShowEditContextInput = signal(false);
    $isActiveFilterOn = signal(false);
    $isActiveFilterOff = signal(false);
    $isPublishedFilterOn = signal(false);
    $isPublishedFilterOff = signal(false);
    $flowInboxDirectories = signal([] as FlowInboxDirectory[]);
    $isCreatingDirectory = signal(false);
    $isDragging = signal(false);
    $currentDirectory = signal(null as FlowInboxDirectory);
    $activeHoverTargetId = signal(null as string);
    // #endregion

    // #region [computeds]
    $currentUser = computed(() => this.authService.$user());
    $isInDirectory = computed(() => this.router.url.includes(Enums.PagesPaths.FlowInboxDirectory));
    $dragTargetIds = computed(() => this.$flowInboxDirectories().map(x => x.id));
    $hasDirectories = computed(() => this.$flowInboxDirectories().length > 0);
    $hasReachedMaxLimit = computed(() => {
        const contextFlowDefinitions = this.$contextModeDispatcher()?.contextId == null
            ? []
            : this.$flowDefinitionContexts().find(x => x.id == this.$contextModeDispatcher()?.contextId)?.flowDefinitions;
        return contextFlowDefinitions?.length == this.CONTEXT_MAX_LIMIT;
    });
    // #endregion

    // #region [effects]
    $isRemovingFromDirectoryTriggerEvent = effect(() => {
        if (!this.$isRemovingFromDirectory()) return;
        this.flowContextWrapperRef.toArray().forEach(item => {
            let flowDefinitionItems = item.nativeElement.querySelectorAll('flow-definition-item');
            let removeButton = item.nativeElement.querySelector('.remove-directory');
            let calc = ([...flowDefinitionItems].length - 1) * 5;
            removeButton.style.right = `${calc}px`;
        });
    }, { allowSignalWrites: true });
    // #endregion

    // #region [Input/Output]
    @Input() _filteredFlowDefinitions: FlowDefinition[];
    @Input() _selectedFlowInboxObject: any;
    @Input() _isRemovingFromDirectory: boolean;
    @Output() _refreshEvent = new EventEmitter<boolean>();
    // #endregion

    constructor(
        route: ActivatedRoute,
        router: Router,
        spinner: NgxSpinnerService,
        toastr: ToastrService,
        private nameFilter: NameFilter,
        private isActiveFilter: IsActiveFilter,
        private isPublishedFilter: IsPublishedFilter,
        private cookieService: CookieService,
        private confirmationService: ConfirmationService,
        private flowDefinitionService: FlowDefinitionService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private flowDefinitionContextService: FlowDefinitionContextService,
        private acessoCidadaoService: AcessoCidadaoService,
        private authService: AuthService,
        private location: Location,
        private flowInboxDirectoryService: FlowInboxDirectoryService
    ) {
        super(route, router, spinner, toastr);

        let querystring = this.route.snapshot.queryParamMap.get('s');
        this.shouldWaitForAcessoCidadaoRenewal = Utils.isNotNullOrEmpty(querystring)
            && atob(decodeURIComponent(querystring)).match(/\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}\.\d{3}z/gi) != null;
    }

    async ngOnInit(skipLoading?: boolean, flowInboxToLoad?: string) {
        if (!skipLoading) {
            this.spinner.show();
        }

        if (flowInboxToLoad != null && this.paramId != null) {
            this.location.replaceState(Enums.PagesPaths.FlowDefinitionList);
        } else if (this.paramId != null) {
            flowInboxToLoad = this.paramId;
        }

        const response = await this.acessoCidadaoService.getUnidadesPerfilAdministrador(this.authService.$user().id);
        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }
        this.adminUnitsList = response.data;

        if (flowInboxToLoad != null) {
            this._selectedFlowInboxObject = this.getOrderedAdminUnitsList().find(x => x.guid == flowInboxToLoad);
        } else {
            this.getLocalStorage();
        }

        try {
            this.initializeCollections(this._filteredFlowDefinitions);
        } finally {
            setTimeout(() => {
                this.$isBootstrapFinished.set(true);
                this.spinner.hide();
                setTimeout(() => this.$isFlowItemVisible.set(true), 200);
            }, 400);
        }
    }

    ngAfterViewInit() {
        let interval = setInterval(() => {
            if (this.$isBootstrapFinished()) {
                this.adjustContextCards();
                clearInterval(interval);
            }
        }, 100);
    }

    async ngOnChanges(changes: SimpleChanges) {
        this.$isRemovingFromDirectory.set(this._isRemovingFromDirectory);

        if (changes['_filteredFlowDefinitions']) {
            await this.ngOnInit(true);
        }
    }

    runFilter() {
        this.$filteredFlowDefinitions.set(this.isPublishedFilter.transform(
            this.isActiveFilter.transform(
                this.nameFilter.transform(
                    this.$flowDefinitions(),
                    this.inputFilterRef?.nativeElement.value
                ),
                this.$isActiveFilterOn(),
                this.$isActiveFilterOff()
            ),
            this.$isPublishedFilterOn(),
            this.$isPublishedFilterOff()
        ));

        this.adjustContextCards();
    }

    hasFilteredFlowDefinition(context: FlowDefinitionContext): boolean {
        for (let item of context.flowDefinitions) {
            if (this.$filteredFlowDefinitions().find(x => x.id == item.id) != null) {
                return true;
            }
        }

        return false;
    }

    isFilteredFlowDefinition(flow: FlowDefinition): boolean {
        return this.$filteredFlowDefinitions().some(x => x.id == flow.id);
    }

    isLastContextIndex(flow: FlowDefinition): boolean {
        return flow.contextIndex == this.getContextFlowDefinitions(flow.contextId).length - 1;
    }

    redirectToAction(event) {
        switch (event.actionType) {
            case Enums.FlowDefinitionItemActionType.Open:
                this.openFlowDefinition(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Instantiate:
                this.instantiate(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.NewVersion:
                this.newVersion(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Clone:
                this.clone(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Publish:
                this.publish(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Activate:
                this.activate(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Deactivate:
                this.deactivate(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Delete:
                this.delete(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.GroupByContext:
                this.groupFlowDefinitionByContext(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.AddItemToContext:
                this.addFlowDefinitionToContext(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.RemoveItemFromContext:
                this.removeFlowDefinitionFromContext(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.MoveUpInContext:
                this.moveUpInContext(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.MoveDownInContext:
                this.moveDownInContext(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Pin:
                this.pin(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.Unpin:
                this.unpin(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.AddItemToDirectoryMode:
                this.addItemToDirectoryMode(event.model);
                break;

            case Enums.FlowDefinitionItemActionType.AddItemToDirectory:
                this.addFlowDefinitionToDirectory(event.model);
                break;
        }
    }

    newFlowDefinition() {
        this.router.navigate([Enums.PagesPaths.FlowDefinition]);
    }

    openFlowDefinition(flowDefinition: FlowDefinition) {
        this.router.navigate([Enums.PagesPaths.FlowDefinition, flowDefinition.id]);
    }

    instantiate(flowDefinition: FlowDefinition) {
        let environment = atob(this.cookieService.get('prodest-eflow-env'));
        if (environment.toLowerCase() == 'loc') {
            open(`${location.protocol}//${location.hostname}:44222${Enums.PagesPaths.FlowDefinition}/${flowDefinition.id}`);
        } else {
            open(`${location.origin.split('admin.').join('')}${Enums.PagesPaths.FlowDefinition}/${flowDefinition.id}`);
        }
    }

    newVersion(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.newVersion.name,
            flowDefinition,
            Enums.Messages.NewVersionConfirmMessage,
            () => this.router.navigate([Enums.PagesPaths.FlowDefinition, flowDefinition.id])
        );
    }

    clone(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.clone.name,
            flowDefinition,
            Enums.Messages.CloneConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    async publish(flowDefinition: FlowDefinition) {
        const response = await this.flowDefinitionService.getById(flowDefinition.id);

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return;
        }

        flowDefinition = response.data;

        if (!(await FlowRules.isFlowDefinitionValid(flowDefinition, flowDefinition.isPublished, this.toastr, this.flowObjectDefinitionService))) return;

        this.executeAction(
            this.publish.name,
            flowDefinition,
            Enums.Messages.PublishConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    activate(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.activate.name,
            flowDefinition,
            Enums.Messages.ActivateConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    deactivate(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.deactivate.name,
            flowDefinition,
            Enums.Messages.DeactivateConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    delete(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.delete.name,
            flowDefinition,
            Enums.Messages.DeleteConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    groupFlowDefinitionByContext(flowDefinition: FlowDefinition) {
        this.confirmationService.confirm({
            message: Enums.Messages.GroupByContextConfirmMessage.replace('{0}', flowDefinition.name),
            accept: () => {
                this.$isContextMode.set(true);
                Utils.setSignal(this.$contextModeDispatcher, flowDefinition);
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    getContextFlowDefinitions(contextId: string): FlowDefinition[] {
        return contextId == null ? [] : this.$flowDefinitionContexts().find(x => x.id == contextId)?.flowDefinitions || [];
    }

    moveUpInContext(flowDefinition: FlowDefinition) {
        this.moveInContext(flowDefinition, false);
    }

    moveDownInContext(flowDefinition: FlowDefinition) {
        this.moveInContext(flowDefinition, true);
    }

    pin(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.pin.name,
            flowDefinition,
            Enums.Messages.PinConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    unpin(flowDefinition: FlowDefinition) {
        this.executeAction(
            this.unpin.name,
            flowDefinition,
            Enums.Messages.UnpinConfirmMessage,
            () => this._refreshEvent.emit(true)
        );
    }

    addItemToDirectoryMode(flowInboxDirectory: FlowInboxDirectory) {
        this.confirmationService.confirm({
            message: Enums.Messages.AddItemToDirectoryMessage.replace('{0}', flowInboxDirectory.name),
            accept: () => {
                this.$isContextMode.set(true);
                Utils.setSignal(this.$contextModeDispatcher, flowInboxDirectory);
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    async removeItemFromDirectory(context: FlowDefinitionContext) {
        this.spinner.show();

        for (let flowDefinition of context.flowDefinitions) {
            let response = await this.flowDefinitionService.updateDirectory(flowDefinition.id, null);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
            } else {
                this.toastr.success(response.message.description, Enums.Messages.Success);
            }
        }

        this._refreshEvent.emit(true);

        setTimeout(() => this.spinner.hide(), 500);
    }

    async addFlowDefinitionToDirectory(flowDefinition: FlowDefinition, flowInboxDirectoryId?: string) {
        this.spinner.show();

        let response = await this.flowDefinitionService.updateDirectory(
            flowDefinition.id,
            flowInboxDirectoryId || this.$contextModeDispatcher().id
        );

        if (!response.isSuccess) {
            this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
        } else {
            this.toastr.success(response.message.description, Enums.Messages.Success);
        }

        this._refreshEvent.emit(true);

        setTimeout(() => this.spinner.hide(), 500);
    }

    async addFlowDefinitionToContext(flowDefinition: FlowDefinition) {
        this.spinner.show();

        try {
            let response = await this.flowDefinitionService.addToContext(
                flowDefinition.id,
                this.$contextModeDispatcher() as FlowDefinition
            );

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.toastr.success(response.message.description, Enums.Messages.Success);
            this._refreshEvent.emit(true);
        } finally {
            let dispatcherId = this.$contextModeDispatcher().id;
            await this.exitContextMode();
            setTimeout(() => {
                this.$isContextMode.set(true);
                Utils.setSignal(this.$contextModeDispatcher, this.$flowDefinitions().find(x => x.id == dispatcherId));
            }, 400);
            setTimeout(() => this.spinner.hide(), 800);
        }
    }

    async removeFlowDefinitionFromContext(flowDefinition: FlowDefinition) {
        this.spinner.show();

        try {
            const response = await this.flowDefinitionService.removeFromContext(
                flowDefinition.id,
                this.$contextModeDispatcher() as FlowDefinition
            );

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.toastr.success(response.message.description, Enums.Messages.Success);
            this._refreshEvent.emit(true);
        } finally {
            let contextId = this.$contextModeDispatcher().contextId;
            let dispatcherId = this.$contextModeDispatcher().id;
            await this.exitContextMode();
            setTimeout(() => {
                if (this.$filteredFlowDefinitions().filter(x => x.contextId == contextId).length > 1) {
                    this.$isContextMode.set(true);
                    Utils.setSignal(this.$contextModeDispatcher, this.$flowDefinitions().find(x => x.id == dispatcherId));
                }
            }, 400);
            setTimeout(() => this.spinner.hide(), 800);
        }
    }

    async changeFlowInbox(event) {
        this._selectedFlowInboxObject = event;

        this.setLocalStorage();
        await this.ngOnInit(false, this._selectedFlowInboxObject.guid);
    }

    openFlowInbox() {
        setTimeout(() => {
            (document.querySelector('ngx-select-dropdown .search-container > input') as HTMLElement).focus();
        }, 1);
    }

    hasFilteredFlowItemInContext(contextId: string): boolean {
        let totalFlowDefinitionsInContext = this.$flowDefinitionContexts().find(x => x.id == contextId).flowDefinitions.length;
        let filteredFlowDefinitionsInContext = this.$filteredFlowDefinitions().filter(x => x.contextId == contextId).length;
        return totalFlowDefinitionsInContext != filteredFlowDefinitionsInContext;
    }

    shouldShowEditContext(context: FlowDefinitionContext): boolean {
        return this.$isContextMode()
            && context.id != null
            && context.flowDefinitions.find(x => x.id == this.$contextModeDispatcher().id) != null;
    }

    shouldShowExitContextButton(context: FlowDefinitionContext): boolean {
        return this.$isContextMode()
            && context.flowDefinitions.map(x => x.id).includes(this.$contextModeDispatcher()?.id)
    }

    shouldShowExitDirectoryButton(directory: FlowInboxDirectory): boolean {
        return this.$isContextMode()
            && this.$contextModeDispatcher()?.id == directory.id;
    }

    toggleEditContext(context: FlowDefinitionContext) {
        this.$shouldShowEditContextInput.set(!this.$shouldShowEditContextInput());
        this.$currentEditContextId.set(context.id);
        setTimeout(() => this.editContextInputRef.toArray().forEach(x => x.nativeElement.select()), 1);
    }

    contextNameChange() {
        this.hasContextNameChanged = true;
    }

    getOrderedAdminUnitsList(): any[] {
        return this.adminUnitsList.sort((a, b) => {
            let first = `${a.organizacao.organizacaoPai?.sigla || ''}${a.organizacao.sigla} - ${a.nomeCurto}`;
            let second = `${b.organizacao.organizacaoPai?.sigla || ''}${b.organizacao.sigla} - ${b.nomeCurto}`;
            return first.localeCompare(second);
        }).map(x => Object.assign(x, {
            description: `${x.organizacao.organizacaoPai?.sigla != null ? x.organizacao.organizacaoPai.sigla + ' - ' : ''}${x.organizacao.sigla} - ${x.nomeCurto}`
        }));
    }

    async exitContextMode() {
        let contextId = this.$contextModeDispatcher().contextId;

        // caso haja FlowDefinitions com ordenação alterada
        if (this.reorderedFlowDefinitionIds.length > 0) {
            this.spinner.show(contextId);
            let message = null;

            // filtra apenas os FlowDefinitions cuja ordenação tenha sido alterada
            let contextFlowDefinitions = this.getContextFlowDefinitions(contextId).filter(x => this.reorderedFlowDefinitionIds.includes(x.id));

            for (let item of contextFlowDefinitions) {
                try {
                    // atualiza os FlowDefinitions no banco
                    const response = await this.flowDefinitionService.updateRoot(item);

                    if (!response.isSuccess) {
                        this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                        message = null;
                        this._refreshEvent.emit(true);
                        break;
                    }

                    message = response.message.description;
                } catch (error) {
                    this.toastr.error(Enums.Messages.GeneralError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    console.error(error);
                    message = null;
                    this._refreshEvent.emit(true);
                    break;
                }
            }

            // caso nenhum dos updateRoot() tenha retornado erro
            if (message != null) {
                this.toastr.success(message, Enums.Messages.Success);
                setTimeout(() => this.spinner.hide(contextId), 300);
            }
        }

        // caso o nome do Context tenha sido alterado
        if (this.hasContextNameChanged) {
            this.spinner.show(contextId);

            try {
                const response = await this.flowDefinitionContextService.update(this.$flowDefinitionContexts().find(x => x.id == contextId));

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                } else {
                    this.toastr.success(response.message.description, Enums.Messages.Success);
                }
            } finally {
                this.spinner.hide(contextId);
            }
        }

        // reinicia variáveis de controle do Context Mode
        this.avoidedInitialContextClick = false;
        this.reorderedFlowDefinitionIds = [];
        this.$contextModeDispatcher.set(null);
        this.$shouldShowEditContextInput.set(false);
        this.$currentEditContextId.set(null);
        this.hasContextNameChanged = false;
        this.$isContextMode.set(false);
    }

    dragReleased(event) {
        if (this.$activeHoverTargetId() == null) return;

        this.spinner.show();

        const element = event.source.element.nativeElement;
        element.style.display = 'none';
    }

    async dragDropped(event: any, flowInboxDirectoryId: string) {
        if (event.event.target.closest(`[id="${flowInboxDirectoryId}"]`) == null) return;

        for (let item of event.item.data.flowDefinitions) {
            await this.addFlowDefinitionToDirectory(item, flowInboxDirectoryId);
        }

        this.$activeHoverTargetId.set(null);
    }

    dragStarted() {
        this.$isDragging.set(true);
    }

    dragEnded() {
        this.$isDragging.set(false);
    }

    // #region [FlowInboxDirectory]
    async loadDirectories() {
        const response = await this.flowInboxDirectoryService.get();
        const unitId = atob(window.localStorage.getItem(Enums.LocalStorageKeys.AdminFlowInbox));
        if (response.isSuccess) {
            const directories = response.data.filter(directory => directory.flowInboxId == unitId);
            Utils.setSignal(this.$flowInboxDirectories, directories);
        }
    }

    async openDirectory(directory: FlowInboxDirectory) {
        this.router.navigate([Enums.PagesPaths.FlowInboxDirectory, directory.id]);
    }

    prepareCreationDirectory() {
        this.$isCreatingDirectory.set(true);
    }

    cancelCreationDirectory() {
        this.$isCreatingDirectory.set(false);
    }

    async saveDirectory(name: string) {
        const directory: FlowInboxDirectory = {
            id: undefined,
            name,
            flowInboxId: this._selectedFlowInboxObject.guid,
            createDate: undefined,
            flowDefinitions: []
        };

        const response = await this.flowInboxDirectoryService.insert(directory);
        if (response.isSuccess) {
            await this.loadDirectories();
            this.$isCreatingDirectory.set(false);
        }
    }

    async updateDirectory(newName: string, directory: FlowInboxDirectory) {
        const updatedDto = { ...directory, name: newName };

        const response = await this.flowInboxDirectoryService.update(updatedDto);
        if (response.isSuccess) {
            Utils.updSignal(this.$flowInboxDirectories, x => {
                return x.map(f => f.id === directory.id ? { ...f, name: newName } : f);
            });
        }
    }

    async deleteDirectory(directory: FlowInboxDirectory) {
        const hasItems = !!directory.flowDefinitions.length;

        this.confirmationService.confirm({
            message: hasItems
                ? Enums.Messages.DeleteDirectoryConfirmMessage
                : Enums.Messages.DeleteEmptyDirectoryConfirmMessage.replace('{0}', directory.name),
            accept: async () => {
                if(hasItems) {
                    directory.flowDefinitions.forEach(flow => {
                        this.flowDefinitionService.updateDirectory(flow.id);
                    });
                }

                const response = await this.flowInboxDirectoryService.delete(directory.id);
                if (response.isSuccess) {
                    Utils.updSignal(this.$flowInboxDirectories, x => {
                        return x.filter(f => f.id !== directory.id);
                    });

                    this._refreshEvent.emit(true);
                }
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    getFilteredContext(): FlowDefinitionContext[] {
        let flowDefintionContext;

        if(this.$isInDirectory()) {
            flowDefintionContext = this.$flowDefinitionContexts().filter(context =>
                context.flowDefinitions.some(flow => flow.flowInboxDirectoryId == this.$currentDirectory().id));
        } else {
            flowDefintionContext = this.$flowDefinitionContexts().filter(context =>
                context.flowDefinitions.some(flow => Utils.isNullOrEmpty(flow.flowInboxDirectoryId)));
        }

        return flowDefintionContext;
    }
    // #endregion

    // #region [===== private methods =====]
    // #endregion

    private initializeCollections(responseData: FlowDefinition[]) {
        Utils.setSignal(this.$flowDefinitions, responseData);
        this.$flowDefinitionContexts.set([]);

        this.$flowDefinitions().forEach(item => {
            if (item.contextId == null) {
                Utils.updSignal(this.$flowDefinitionContexts, x => {
                    x.push({ flowDefinitions: [item] });
                    return x;
                });
            } else {
                let context = this.$flowDefinitionContexts().find(x => x.id == item.contextId);
                if (context == null) {
                    Utils.updSignal(this.$flowDefinitionContexts, x => {
                        x.push({ id: item.contextId, flowDefinitions: [item] });
                        return x;
                    });
                } else {
                    context.flowDefinitions.push(item);
                }
            }
        });

        Utils.updSignal(this.$flowDefinitionContexts, x => {
            x.forEach(async item => {
                item.flowDefinitions.sort((a, b) => a.contextIndex - b.contextIndex);

                if (item.id != null) {
                    const response = await this.flowDefinitionContextService.getById(item.id);

                    if (!response.isSuccess) {
                        this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                        return;
                    }

                    item.name = response.data.name;
                }
            });
            return x;
        });

        this.loadDirectories();

        this.runFilter();
    }

    private executeAction(
        action: string,
        flowDefinition: FlowDefinition,
        confirmationMessage: string,
        callbackAfterSuccess: Function
    ) {
        this.confirmationService.confirm({
            message: confirmationMessage.replace('{0}', flowDefinition.name),
            accept: async () => {
                this.spinner.show();

                const response = await this.flowDefinitionService[action](flowDefinition.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.toastr.success(response.message.description, Enums.Messages.Success);
                callbackAfterSuccess();
            },
            reject: () => {
                this.confirmationService.close();
            }
        });
    }

    // resolve o layout dos flow-definition-items quando pertencentes a um Context
    private adjustContextCards() {
        setTimeout(() => {
            let contexts = this.contextRef.toArray();
            if (contexts == null) return;

            contexts.forEach(item => {
                let context = item.nativeElement;
                let flowItems = this.flowItemRef.toArray().filter(x => [...context.children].includes(x.element.nativeElement));
                const flowItemOffset = Number.parseInt(SCSS_VARS['$context-card-top-offset'].replace('px', ''));

                if (flowItems.length > 1) {
                    let totalHeight = 0;
                    flowItems.forEach(flowItem => totalHeight += flowItem.element.nativeElement.offsetHeight);

                    const totalOffset = (flowItems.length - 1) * flowItemOffset;

                    // calcula a altura total do container (div) do Context
                    context.style.height = `${totalHeight + totalOffset}px`;
                }

                flowItems.forEach((item, idx) => {
                    // posiciona os flow-definition-items relativamente ao container (div) do Context
                    // flow-definition-items já estão ordenados pelo "contextIndex"; seguir o "idx" evita problemas com flow-definition-items filtrados
                    let contextIndex = idx;
                    let flowItemElement = item.element.nativeElement.children[0] as HTMLElement;
                    flowItemElement.style.top = contextIndex == null || contextIndex == 0 ? null : `${contextIndex * flowItemOffset}px`;

                    // aplica efeito de profundidade/offset aos flow-definition-items inferiores
                    let sideOffset = 5; /* px */
                    flowItemElement.style.width = `calc(100% - ${sideOffset * 2 * contextIndex}px)`;
                    flowItemElement.style.marginLeft = `${sideOffset * contextIndex}px`;
                });
            });
        }, 1);
    }

    private moveInContext(flowDefinition: FlowDefinition, isDownwards = false) {
        // contorna artefatos visuais durante a animação de deslocamento dos flow-definition-items
        this.spinner.show(flowDefinition.contextId);
        let flowItems = this.flowItemRef.toArray().filter(x => x._model.contextId == flowDefinition.contextId);
        flowItems.forEach(x => x.element.nativeElement.children[0].style.opacity = '0');

        setTimeout(() => {
            // reordena os flow-definition-items no DOM
            let contextFlowDefinitions = this.getContextFlowDefinitions(flowDefinition.contextId);
            moveItemInArray(contextFlowDefinitions, flowDefinition.contextIndex, isDownwards ? flowDefinition.contextIndex + 1 : flowDefinition.contextIndex - 1);

            // reflete as mudanças de ordenação no ContextIndex de cada FlowDefinition afetado
            contextFlowDefinitions.forEach((item, idx) => {
                if (item.contextIndex != idx) {
                    this.reorderedFlowDefinitionIds.push(item.id);
                    item.contextIndex = idx;
                }
            });
            this.adjustContextCards();
        }, 300);

        // contorna artefatos visuais durante a animação de deslocamento dos flow-definition-items
        setTimeout(() => {
            flowItems.forEach(x => x.element.nativeElement.children[0].style.opacity = '1');
            this.spinner.hide(flowDefinition.contextId);
        }, 500);
    }

    private getLocalStorage() {
        // lê os valores do storage de filtros
        let filterStorage = window.localStorage.getItem(Enums.LocalStorageKeys.AdminFilter);
        let filterValues = [];
        if (filterStorage != null) {
            filterValues = atob(filterStorage).split(';');
        }

        // caso o storage exista
        if (filterValues.length >= 1) {
            // atribui os valores parseados do storage
            this.$isActiveFilterOn.set(!!JSON.parse(String(filterValues[0].split(':')[1]).toLowerCase()));
            this.$isActiveFilterOff.set(!!JSON.parse(String(filterValues[0].split(':')[2]).toLowerCase()));
            this.$isPublishedFilterOn.set(!!JSON.parse(String(filterValues[1].split(':')[1]).toLowerCase()));
            this.$isPublishedFilterOff.set(!!JSON.parse(String(filterValues[1].split(':')[2]).toLowerCase()));
        } else {
            // valores padrão dos filtros de "Ativos" e "Publicados"
            this.$isActiveFilterOn.set(true);
            this.$isActiveFilterOff.set(false);
            this.$isPublishedFilterOn.set(false);
            this.$isPublishedFilterOff.set(false);

            this.setLocalStorage();
        }

        // lê os valores do storage da Caixa de Fluxos
        let inboxStorage = window.localStorage.getItem(Enums.LocalStorageKeys.AdminFlowInbox);
        let inboxValue = '';
        if (inboxStorage != null) {
            inboxValue = atob(inboxStorage);
        }

        // caso o storage não exista
        if (inboxValue == '') {
            if (this.adminUnitsList.length > 0) {
                this._selectedFlowInboxObject = this.getOrderedAdminUnitsList()[0];

                this.setLocalStorage();
            } else {
                this.toastr.error(Enums.Messages.NoFlowInboxError, Enums.Messages.Error, Utils.getToastrErrorOptions());
                console.error(Enums.Messages.NoFlowInboxError);
            }
        } else {
            let flowInbox = this.getOrderedAdminUnitsList().find(x => x.guid == inboxValue);
            if (flowInbox != null) {
                this._selectedFlowInboxObject = this.getOrderedAdminUnitsList().find(x => x.guid == inboxValue);
            } else {
                this._selectedFlowInboxObject = this.getOrderedAdminUnitsList()[0];
            }
        }
    }

    private setLocalStorage() {
        // monta a string de valores no formato esperado
        let filterValues = `active:${this.$isActiveFilterOn()}:${this.$isActiveFilterOff()}`;
        filterValues += `;published:${this.$isPublishedFilterOn()}:${this.$isPublishedFilterOff()}`;

        // armazena os valores no storage de filtros
        window.localStorage.setItem(Enums.LocalStorageKeys.AdminFilter, btoa(filterValues));

        // armazena os valores no storage de Caixa de Fluxos
        window.localStorage.setItem(Enums.LocalStorageKeys.AdminFlowInbox, btoa(this._selectedFlowInboxObject.guid || ""));
    }
}
